import React, { useState, useCallback } from 'react';
import { closeModal, openToast } from 'utils/localDispatcher';
import { StartDeliveryModalStyle } from './StartDeliveryModalStyle';
import { DeliveryCompany } from '__generated__/commerce';
import { CloseIcon, ErrorIcon } from 'utils/SVGIcons';

interface StartDeliveryModalProps {
  storeOrderId: string;
  onClose?: () => void;
  onSubmit: (params: {
    storeOrderId: string;
    company: DeliveryCompany | null;
    invoice: string;
    onSuccess: () => void;
    onFailure: (errorType: string) => void;
  }) => void;
  loading?: boolean;
}

const DELIVERY_COMPANIES = [
  { value: DeliveryCompany.Cj, label: 'CJ대한통운' },
  { value: DeliveryCompany.Hanjin, label: '한진택배' },
  { value: DeliveryCompany.Lotte, label: '롯데택배' },
  { value: DeliveryCompany.Logen, label: '로젠택배' },
  { value: DeliveryCompany.PostOffice, label: '우체국택배' },
  { value: DeliveryCompany.Gyeongdong, label: '경동택배' },
  { value: DeliveryCompany.Daeshin, label: '대신택배' },
  { value: DeliveryCompany.Ilyang, label: '일양로지스' },
  { value: DeliveryCompany.Geonyoung, label: '건영택배' },
  { value: DeliveryCompany.Nonghyup, label: '농협택배' },
  { value: DeliveryCompany.Slx, label: 'SLX' },
  { value: DeliveryCompany.Yongma, label: '용마로지스' },
];

const StartDeliveryModal = ({
  storeOrderId,
  onClose,
  onSubmit,
  loading = false,
}: StartDeliveryModalProps) => {
  const [company, setCompany] = useState<DeliveryCompany | ''>('');
  const [invoice, setInvoice] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    } else {
      closeModal();
    }
  }, [onClose]);

  const handleCompanyChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as DeliveryCompany | '';
    setCompany(value);
    setError(null);
  }, []);

  const handleInvoiceChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInvoice(e.target.value);
    setError(null);
  }, []);

  const handleSubmit = useCallback(() => {
    if (!company || !invoice.trim()) return;

    onSubmit({
      storeOrderId,
      company: company as DeliveryCompany,
      invoice: invoice.trim(),
      onSuccess: () => {
        openToast('배송시작 알림을 보냈어요.');
        handleClose();
      },
      onFailure: (errorType: string) => {
        if (errorType === 'StartDeliveryInvalidInvoiceError') {
          setError('확인되지 않는 운송장 번호예요.');
        } else if (errorType === 'StartDeliveryInvalidDeliveryStatusError') {
          setError('이미 배송이 시작된 주문이에요.');
        } else if (errorType === 'StartDeliveryInvalidOrderStatusError') {
          setError('배송을 시작할 수 없는 주문 상태예요.');
        } else {
          setError('잠시 문제가 발생했어요.');
        }
      },
    });
  }, [company, invoice, storeOrderId, onSubmit, handleClose]);

  const isSubmitDisabled = !company || !invoice.trim() || loading;

  return (
    <StartDeliveryModalStyle>
      <button type="button" className="close-button" onClick={handleClose}>
        <CloseIcon size={24} />
      </button>

      <div className="title">배송시작 알림</div>
      <div className="description">상품 배송을 시작했다면 고객에게 알려주세요.</div>

      <div className="input-label">운송장 번호</div>
      <div className="input-row">
        <select value={company} onChange={handleCompanyChange}>
          <option value="">택배사 선택</option>
          {DELIVERY_COMPANIES.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
        <div className="input-wrapper">
          <input
            type="text"
            value={invoice}
            onChange={handleInvoiceChange}
            placeholder="운송장 번호"
            className={error ? 'has-error' : ''}
          />
          {error && (
            <div className="error-message">
              <ErrorIcon size={16} />
              <span>{error}</span>
            </div>
          )}
        </div>

      </div>


      <div className="button-row">
        <button type="button" className="cancel-button" onClick={handleClose}>
          취소
        </button>
        <button
          type="button"
          className="submit-button"
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
        >
          배송시작 알림 보내기
        </button>
      </div>
    </StartDeliveryModalStyle>
  );
};

export default StartDeliveryModal;
